/**
 * A cost weighted Pareto's words (B2 T2, docs/plans/2026-10-18_actionable-platform.md; platform-core
 * twin/brief_money_rank.go): where the plant priced its scrap, the briefs rank their defects by what they cost (each
 * piece at its part's cost per rejected casting, else the plant's) and the money stands beside the pieces, in its
 * fact's words (money.pareto.<defect>.cost); where nothing is priced, the Pareto keeps pieces and the page links to the
 * cost. A brief composed before it says nothing of its ranking, and the page says nothing either. No dash anywhere.
 */
import type { BriefFact } from "@/lib/api/brief";
import type { ActionsMoney } from "@/lib/api/actions";
import type { DefectGroup } from "@/lib/actions/list";
import { count, factTextOf } from "./words";

/** The id of the org's cost card on Settings, Organisation. */
export const SET_COST_ANCHOR = "scrap-cost";

/** Where the org's cost of scrap is set: Settings, Organisation, its cost card (an org admin's). */
export const SET_COST_HREF = `/app/settings?tab=organisation#${SET_COST_ANCHOR}`;

/** The words of the link to it. */
export const SET_COST_WORDS = "set a cost to rank by money";

/** What a member who is not an org admin is told instead: the cost card is not theirs to see, so no link to it. */
export const SET_COST_ASK_WORDS = "ask an org admin to set a cost to rank by money";

/** The element a Settings address names to scroll to (its hash), or null: the app does not scroll to a hash itself. */
export function scrollTargetOf(hash: string): string | null {
  return hash === `#${SET_COST_ANCHOR}` ? SET_COST_ANCHOR : null;
}

type Ranked = { ranked_by?: "money" | "pieces" } | null | undefined;

/** The Pareto is ranked by money. */
export const rankedByMoney = (s: Ranked): boolean => s?.ranked_by === "money";

/** The Pareto is ranked by pieces because nothing is priced (never said of a brief composed before the ranking). */
export const rankedByPieces = (s: Ranked): boolean => s?.ranked_by === "pieces";

/** A sum as the server's facts write it ("USD 124,500"). */
const moneyWords = (v: number, currency: string) => `${currency} ${count(v)}`;

/** A cost and the pieces it could price, in words: the cost alone where it priced them all ("USD 6,020"), else "USD 6,020 on 22 of 25 pieces". */
function costOnPieces(money: string, priced: number | undefined, pieces: number): string {
  return priced === undefined || priced >= pieces ? money : `${money} on ${count(priced)} of ${count(pieces)} pieces`;
}

/**
 * A Pareto row's money: its cost in its fact's words, with the pieces it could price where some could not ("USD 6,020
 * on 22 of 25 pieces": a cost that prices only some of the pieces says so), "not priced" where none could, null where
 * the brief is not ranked by money. A row with no pieces has nothing to price and says nothing.
 */
export function paretoMoneyText(
  row: { defect: string; pieces: number; cost?: number; priced_pieces?: number },
  currency: string | undefined,
  facts: readonly BriefFact[] | undefined,
): string | null {
  if (row.priced_pieces === undefined) return null;
  if (row.cost === undefined) return row.pieces > 0 ? "not priced" : null;
  const money = factTextOf(facts)(`money.pareto.${row.defect}.cost`, moneyWords(row.cost, currency ?? ""));
  return costOnPieces(money, row.priced_pieces, row.pieces);
}

/**
 * A defect card's money, from the brief's facts (money.pareto.<defect>.cost and .priced): the cost in its fact's
 * words, with the pieces it priced where that is fewer than the defect's; "" where the brief states no cost for it.
 */
export function factMoneyText(defect: string, pieces: number, facts: readonly BriefFact[] | undefined): string {
  const cost = facts?.find((f) => f.key === `money.pareto.${defect}.cost`);
  if (!cost) return "";
  const priced = facts?.find((f) => f.key === `money.pareto.${defect}.priced`);
  return costOnPieces(cost.text, priced?.value, pieces);
}

/** The action list's defect groups, the dearest first where the brief is ranked by money; else as they are. */
export function rankByMoney(groups: DefectGroup[], money: ActionsMoney | undefined): DefectGroup[] {
  if (!money || money.ranked_by !== "money") return groups;
  const cost = (g: DefectGroup) => money.by_defect[g.defect]?.cost;
  return groups
    .map((g, i) => ({ g, i }))
    .sort((a, b) => {
      const ca = cost(a.g);
      const cb = cost(b.g);
      if ((ca === undefined) !== (cb === undefined)) return ca === undefined ? 1 : -1;
      if (ca !== undefined && cb !== undefined && ca !== cb) return cb - ca;
      return a.i - b.i;
    })
    .map((x) => x.g);
}

/**
 * The action list's defect groups as the list says they are ranked (the 2026-10-24 sweep, item 15.2): the dearest first
 * where the brief is ranked by money (rankByMoney), the most pieces first where it is ranked by pieces, which the note
 * above the groups says ("Ranked by pieces"). A group with no pieces of its own, and the one tied to no defect, come
 * after, each kept in its order. As they are without the money (an older server, which says no ranking).
 */
export function rankDefectGroups(groups: DefectGroup[], money: ActionsMoney | undefined): DefectGroup[] {
  if (!money) return groups;
  if (money.ranked_by === "money") return rankByMoney(groups, money);
  const pieces = (g: DefectGroup) => (g.defect ? money.by_defect[g.defect]?.pieces : undefined);
  return groups
    .map((g, i) => ({ g, i }))
    .sort((a, b) => {
      const pa = pieces(a.g);
      const pb = pieces(b.g);
      if ((pa === undefined) !== (pb === undefined)) return pa === undefined ? 1 : -1;
      if (pa !== undefined && pb !== undefined && pa !== pb) return pb - pa;
      return a.i - b.i;
    })
    .map((x) => x.g);
}

/**
 * A defect group's figures on the action list: "USD 6,020, 20 pieces", "USD 6,020 on 22 of 25 pieces" where the cost
 * prices only some of them, "40 pieces, not priced", "100 pieces".
 */
export function defectGroupMoneyText(defect: string, money: ActionsMoney | undefined): string | null {
  const d = money?.by_defect[defect];
  if (!money || !d) return null;
  const pieces = `${count(d.pieces)} pieces`;
  // A defect with no pieces in the window has nothing to price: never "0 pieces, not priced".
  if (money.ranked_by !== "money" || d.pieces === 0) return pieces;
  if (d.cost === undefined) return `${pieces}, not priced`;
  const cost = moneyWords(d.cost, money.currency ?? "");
  return d.priced !== undefined && d.priced < d.pieces ? costOnPieces(cost, d.priced, d.pieces) : `${cost}, ${pieces}`;
}
